import React, { FunctionComponent } from 'react';
import { faPen, faTrash, faBell, faSms, faPaperPlane, faUserCheck } from '@fortawesome/pro-regular-svg-icons';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { ThreeDotsDropdownOption } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdownOption';
import Client, { LeadSource, LeadStage } from '@spike/client-model';
import { formatPhoneNumber } from '@spike/phone-utils';
import { useMarketplace } from 'hooks';
import { useLeadsActions } from '../LeadsProvider';
import styles from './LeadRow.module.scss';
import clsx from 'clsx';

interface LeadRowProps {
    lead: Client;
    isLast: boolean;
}

const LeadRow: FunctionComponent<LeadRowProps> = ({ lead, isLast }) => {
    const marketplace = useMarketplace();
    const { openEditForm, openBooking, openSms, openShareSendPanel, openConvert, openDetail } = useLeadsActions();

    const getFullName = () => {
        const fullName = `${lead.firstName} ${lead.lastName}`.trim();
        return fullName || '-';
    };

    const getLeadAge = (): string => {
        if (lead.leadStage === LeadStage.Won && lead.clientSince) {
            return lead.clientSince.format('MM/DD/YYYY');
        }
        if (lead.createdAt) {
            const days = Math.floor((Date.now() - lead.createdAt.toDate().getTime()) / (1000 * 60 * 60 * 24));
            return days === 1 ? '1 day' : `${days} days`;
        }
        return '-';
    };

    const getLastContactedDisplay = () => {
        if (!lead.lastContactedAt) return '-';
        return lead.lastContactedAt.format('MM/DD/YYYY HH:mm');
    };

    const SOURCE_LABELS: Partial<Record<LeadSource, string>> = {
        [LeadSource.Manual]: 'Manually',
        [LeadSource.IntakeForm]: 'Intake Form',
        [LeadSource.AbandonedCart]: 'Abandoned Cart',
        [LeadSource.UnknownSms]: 'Unknown Number',
        [LeadSource.Import]: 'Import'
    };

    const getSourceDisplay = (): string => {
        if (!lead.leadSource) return '-';
        return SOURCE_LABELS[lead.leadSource] ?? String(lead.leadSource).replace(/_/g, ' ');
    };

    const getStageColor = (stage: LeadStage | undefined): string => {
        const stageColors: Partial<Record<LeadStage, string>> = {
            [LeadStage.New]: styles.stageNew,
            [LeadStage.Contacted]: styles.stageContacted,
            [LeadStage.Booked]: styles.stageBooked,
            [LeadStage.Won]: styles.stageWon,
            [LeadStage.Lost]: styles.stageLost
        };
        return (stage && stageColors[stage]) || styles.stageNew;
    };

    const getStageLabel = (stage: LeadStage | undefined): string => {
        if (!stage) return 'New';
        const labels: Partial<Record<LeadStage, string>> = {
            [LeadStage.New]: 'New',
            [LeadStage.Contacted]: 'Contacted',
            [LeadStage.Booked]: 'Booked',
            [LeadStage.Won]: 'Won',
            [LeadStage.Lost]: 'Lost'
        };
        return labels[stage] ?? String(stage);
    };

    const dropdownOptions: ThreeDotsDropdownOption[] = [
        { id: 'book-now', label: 'Book Now', icon: faBell, onClick: () => openBooking(lead) },
        { id: 'edit', label: 'Edit', icon: faPen, onClick: () => openEditForm(lead) },
        ...(lead.phone ? [{ id: 'contact', label: 'Send SMS', icon: faSms, onClick: () => openSms(lead) }] : []),
        ...(!lead.clientSince
            ? [
                  {
                      id: 'convert-to-client',
                      label: 'Convert to Client',
                      icon: faUserCheck,
                      onClick: () => openConvert(lead)
                  }
              ]
            : []),
        ...(lead.pets.length === 0 || !lead.firstName || !lead.lastName
            ? [
                  {
                      id: 'intake-form',
                      label: 'Send Intake Form',
                      icon: faPaperPlane,
                      onClick: () => openShareSendPanel(lead)
                  }
              ]
            : [])
    ];

    return (
        <TableRow
            className={styles.desktopRow}
            onClick={() => openDetail(lead)}
        >
            <TableCell className={styles.nameMainCell}>
                <div className={styles.nameCell}>
                    <p className={styles.leadName}>{getFullName()}</p>
                </div>
            </TableCell>
            <TableCell>
                <div className={styles.petsCell}>
                    {!lead.pets || lead.pets.length === 0
                        ? '-'
                        : lead.pets.map((p, i) => (
                              <span
                                  key={p.id}
                                  className={styles.petTag}
                              >
                                  {p.name}
                                  {i < lead.pets.length - 1 && ','}
                              </span>
                          ))}
                </div>
            </TableCell>
            <TableCell className={styles.contactCell}>
                <div className={styles.contactContent}>
                    {lead.email && <div>{lead.email}</div>}
                    {lead.phone && <div>{formatPhoneNumber(lead.phone, marketplace.basics.address.country!.id)}</div>}
                    {!lead.email && !lead.phone && <div>-</div>}
                </div>
            </TableCell>
            <TableCell>{getLastContactedDisplay()}</TableCell>
            <TableCell>{getSourceDisplay()}</TableCell>
            <TableCell>{getLeadAge()}</TableCell>
            <TableCell>
                <div className={clsx(styles.stageChip, getStageColor(lead.leadStage))}>
                    {getStageLabel(lead.leadStage)}
                </div>
            </TableCell>
            <TableCell
                className={styles.actionsCell}
                isActions
                onClick={event => event.stopPropagation()}
            >
                <ThreeDotsDropdown
                    options={dropdownOptions}
                    direction={isLast ? 'up' : 'down'}
                />
            </TableCell>
        </TableRow>
    );
};

export default LeadRow;
